import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Loading } from "../../../components/Helper/Loading";
import { refresh as refreshLayerCache } from "../../../offline/layerCache";
import { getFilledArrayOrDefault, isFilledArray } from "../../../utils";
import { getAllActiveTenantConfigs, getAllTenantDefects, getAllTenantFieldOptions } from "../actions";
import { useFacilityLocations } from "../../../hooks/useFacilityLocationQueries";
import { applyDisplayFilter, applyDisplayFilterFields, applyDisplayFilterLayerFields, setupFieldClassFromConfig } from "./applyDisplayFilter";
import { setDefaultDateFormats } from "../../../components/scenes/Layers/fields/formatters";
import {
    CONFIG_TYPES,
    baseConfig,
    tenantIds
} from "./constants";
import { RootConfigType } from "./constantsTyped";
import { getRipewiseConfig } from "./ripewise";
import { BakkerConfig } from "./tenants/Bakker";
import { ExperienceDataConfig } from "./tenants/ExperienceData";
import { MasterTenantConfig } from "./tenants/MasterTenant";
import { MehadrinConfig } from "./tenants/Mehadrin";
import { MehadrinISRConfig } from "./tenants/MehadrinISR";
import { MehadrinNLConfig } from "./tenants/MehadrinNL";
import { MissionBredaConfig } from "./tenants/MissionBreda";
import { SHPTropicalConfig } from "./tenants/SHPTropical";

const ConfigContext = createContext(baseConfig);
export const rootConfigs: Record<string, any> = {
    [tenantIds.ExperienceData]: ExperienceDataConfig,
    [tenantIds.Bakker]: BakkerConfig,
    [tenantIds.BakkerBelgium]: BakkerConfig,
    [tenantIds.SHPTropical]: SHPTropicalConfig,
    [tenantIds.EDMissionBreda]: MissionBredaConfig, // This the tenant we created for them
    [tenantIds.ActualMission]: MissionBredaConfig, // This the tenant they own
    [tenantIds.MasterTenant]: MasterTenantConfig,
    [tenantIds.Mehadrin_mtexukcom]: MehadrinConfig,
    [tenantIds.Mehadrin_mtexnl]: MehadrinNLConfig,
    [tenantIds.Mehadrin_isr]: MehadrinISRConfig,
};


// * This component is a provider used MainContent -> RequireAuth -> ConfigProver to set a global config state
export function ConfigProvider({ tenant_id, refreshInterval = 6 * 60 * 1000, children }: { tenant_id?: string, refreshInterval?: false | number, children: JSX.Element }) {
    const dispatch = useDispatch();
    const user = useSelector((state: any) => state?.auth?.user);
    const facility = user?.tenant_user?.facility;
    const options_by_field = useSelector((state: any) => state?.tenants?.options_by_field);
    const params = useParams();

    // * Get tenant_id from the url params or the user object, use that as an proxy to determine if we are on a tenant edit page
    const param_tenant_id = params?.tenant_id;
    tenant_id = (tenant_id || param_tenant_id || user.tenant?.tenant_id) as string;


    // `undefined` until getAllActiveTenantConfigs SUCCESS writes this tenant's
    // array — that distinction is exposed as `configs_loaded` below so pages
    // can tell "configs still fetching" apart from "flag genuinely off".
    const raw_tenant_configs = useSelector((state: any) => state?.tenants?.configs)?.[tenant_id];
    let configs = raw_tenant_configs || [];
    const { data: facility_locations = [] } = useFacilityLocations({ offset: 0, limit: 10000 });


    // * timer to refresh the active configs and form options list every 5 min
    const timer = useRef<any>(null);

    const getOptions = (tenant_id: string) => {
        if (tenant_id) {
            dispatch(getAllTenantFieldOptions(tenant_id) as any);
        }
    };

    const getConfigs = (tenant_id: string) => {
        if (tenant_id) {
            dispatch(getAllActiveTenantConfigs(tenant_id) as any);
        }
    };
    const getDefects = (tenant_id: string) => {
        if (tenant_id) {
            dispatch(getAllTenantDefects(tenant_id) as any);
        }
    };
    // Pre-download the offline pallet directory (no-op for tenants without
    // enable_offline_checks; refreshLayerCache short-circuits internally).
    // See offline/layerCache.ts.
    const getLayerCache = () => {
        refreshLayerCache();
    };
    // Fetch the options & configs on load.
    //
    // Note for offline-checks: this effect fires *before* <OfflineServiceWorker />
    // has had a chance to mirror `enable_offline_checks` into the module-level
    // flag that the etag interceptor and refreshLayerCache read. So on first
    // login, the interceptor short-circuits on these very first responses
    // (etag-cache stays empty) and refreshLayerCache short-circuits at its
    // own guard. The race is closed in <OfflineServiceWorker /> by re-firing
    // the same URLs once the flag is mirrored — see warmETagCache and
    // layerCache.refresh() invocations there. Don't try to fix the race here
    // by gating these dispatches on the flag — non-offline tenants need the
    // configs immediately, and the offline path tolerates the duplicated
    // request. Design §5.5.1.
    useEffect(() => {
        if (tenant_id) {
            getOptions(tenant_id);
            getConfigs(tenant_id);
            getDefects(tenant_id);
            getLayerCache();
        }
    }, [tenant_id]);

    // Setup an interval on non-config pages
    useEffect(() => {
        if (!param_tenant_id && tenant_id && typeof refreshInterval === "number" && refreshInterval > 0) {
            timer.current = setInterval(() => {
                if (tenant_id !== undefined) { // fuck u typescript
                    getOptions(tenant_id);
                    getConfigs(tenant_id);
                    getDefects(tenant_id);
                    getLayerCache();
                }
            }, refreshInterval as number);
        }

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
        };
    }, [tenant_id, param_tenant_id, refreshInterval]);


    if (!configs) {
        return <Loading />;
    }

    // * Get hard coded config
    const rootConfig = (rootConfigs[tenant_id] || baseConfig) as any;


    const value = useMemo(() => {
        // We are only intrested in the actual configs of these types
        // But for management purposes we store an raw array of configs in `configs`
        configs = configs.sort((a, b) => a.position - b.position);
        const raw_root_config = configs.find((i) => i.type === CONFIG_TYPES.ROOT && i.value === "root")?.config || {};
        // * EFQ-3850 — single source of truth for tenant date/datetime formats.
        // * Resolution order: tenant root config (DB) → hardcoded base config →
        // * library default. Empty strings are treated as unset.
        const pickFormat = (...candidates: any[]) => candidates.find(
            (c) => typeof c === "string" && c.length > 0
        );
        const tenant_date_format = pickFormat(raw_root_config?.date_format, rootConfig?.date_format) || "DD-MM-YYYY";
        const tenant_datetime_format = pickFormat(raw_root_config?.datetime_format, rootConfig?.datetime_format) || "HH:mm DD-MM-YYYY";
        const root_config = {
            ...raw_root_config,
            date_format: tenant_date_format,
            datetime_format: tenant_datetime_format,
        };
        const rootDateTimeFormats = {
            date_format: tenant_date_format,
            datetime_format: tenant_datetime_format,
        };
        const layer_types = configs.filter((i) => i.type === CONFIG_TYPES.LAYER_TYPE).map((i) => ({ ...i.config, position: i.position }));
        const stages = configs.filter((i) => i.type === CONFIG_TYPES.STAGE).map((i) => i.config);
        const check_locations = configs.filter((i) => i.type === CONFIG_TYPES.CHECK_LOCATION).map((i) => ({ ...i.config, position: i.position }));
        const pdf_reports = configs.filter((i) => i.type === CONFIG_TYPES.PDF_REPORT).map((i) => i.config);
        const fruit_types = configs.filter((i) => i.type === CONFIG_TYPES.FRUIT_TYPE).map((i) => i.config).map((i) => ({ ...i, ripening_fields: applyDisplayFilterFields(i.ripening_fields, { facility }) }));
        const color_codes = configs.filter((i) => i.type === CONFIG_TYPES.COLOR_CODE).map((i) => i.config);
        const ripewise_config = getRipewiseConfig(configs);


        const form_options = (() => {
            // * Setup form options by combining the inline options and the database options
            const allFormFieldsWithOptions = getFilledArrayOrDefault(configs).flatMap((i) => i.config).flatMap((i) => Object.values(i)).filter((i) => isFilledArray(i))
                .flatMap((i) => i)
                .filter((i: any) => i.field && i.field?.name && isFilledArray(i.field.options))
                .map((i: any) => ({ [i.field.name]: i.field.options }));

            const inlineOptions = allFormFieldsWithOptions.reduce((acc, i) => {
                Object.entries(i).forEach(([key, value]) => {
                    acc[key] = [...(acc[key] || []), ...value]; // Merge existing and new options
                });
                return acc;
            }, {});
            const databaseOptions = options_by_field?.[user.tenant?.tenant_id] || {};
            return Object.keys({ ...inlineOptions, ...databaseOptions }).reduce((acc, key) => {
                acc[key] = [...(inlineOptions[key] || []), ...(databaseOptions[key] || [])];
                return acc;
            }, {});
        })();

        const get_form_option_label = (field: string, value: any) => {
            const options = form_options?.[field] || [];
            return getFilledArrayOrDefault(options).find((i) => i.value === value)?.label || value;
        };
        return {
            ...rootConfig,
            facility_locations,
            layer_types,
            stages,
            check_locations,
            pdf_reports,
            fruit_types,
            root_config,
            ripewise_config,
            color_codes,
            form_options,
            configs,
            // False during the initial fetch after a hard page load. Feature
            // gates that NAVIGATE away when their root_config flag is off
            // (e.g. SignalsPage) must wait for this — on a deep-link the first
            // render sees an empty config and would bounce a perfectly valid
            // URL (EFQ-4145: e2e signals specs flaked exactly this way).
            configs_loaded: raw_tenant_configs !== undefined,
            tenant_id,
            get_form_option_label,
            applyDisplayFilter: (fields, filter: any = {}) => applyDisplayFilter(fields, { facilities: facility ? [facility] : undefined, ...filter }),
            applyDisplayFilterFields: (fields, filter: any = {}) => applyDisplayFilterFields(fields, { facilities: facility ? [facility] : undefined, ...filter }),
            applyDisplayFilterLayerFields: (fields, filter: any = {}) => applyDisplayFilterLayerFields(
                fields,
                { facilities: facility ? [facility] : undefined, ...filter },
                { fruitTypes: fruit_types, stages, rootDateTimeFormats }
            ),
            setupFieldClassFromConfig: (fieldConfig, filter: any = {}) => setupFieldClassFromConfig(
                fieldConfig,
                { facilities: facility ? [facility] : undefined, ...filter },
                { fruitTypes: fruit_types, stages, rootDateTimeFormats }
            ),
        };
    }, [configs.map((i) => i.id).join(","), raw_tenant_configs === undefined, options_by_field, tenant_id, facility, facility_locations]);

    // * EFQ-3850 — push the resolved tenant formats into the `formatters`
    // * module so direct `formatDate(value)` / `formatDateTime(value)`
    // * calls (call sites that don't go through the tenant-config field
    // * pipeline) also inherit the tenant's configured formats.
    useEffect(() => {
        setDefaultDateFormats({
            date_format: value?.root_config?.date_format,
            datetime_format: value?.root_config?.datetime_format,
        });
    }, [value?.root_config?.date_format, value?.root_config?.datetime_format]);


    return <ConfigContext.Provider value={value as RootConfigType}>
        {children}
    </ConfigContext.Provider>;
}

// hook to get the config provided by the context above
// TODO: remove 'any' type here and add type to RootConfigType
export default function useConfig(): RootConfigType {
    const config = useContext(ConfigContext);
    return config;
}

export function useTenantFormOptions() {
    const config = useConfig();
    return config?.form_options || {};
}

// * EFQ-3850 — admin/internal pages (TenantEditConfig, TenantEditOptionsForm,
// * DeviceManagement, ...) must always render dates in ExperienceData's format,
// * regardless of which tenant is currently being viewed/impersonated. The
// * default `formatDate`/`formatDateTime` calls read from a module-level cache
// * that follows whatever tenant's ConfigProvider mounted last, so admin
// * screens need to opt into the ED formats explicitly.
const _ED_HARDCODED_DATE_FORMAT = "DD-MM-YYYY";
const _ED_HARDCODED_DATETIME_FORMAT = "HH:mm DD-MM-YYYY";

export function useEDDateFormats() {
    const dispatch = useDispatch();
    const edConfigs = useSelector(
        (state: any) => state?.tenants?.configs?.[tenantIds.ExperienceData]
    );
    useEffect(() => {
        if (!edConfigs) {
            dispatch(getAllActiveTenantConfigs(tenantIds.ExperienceData) as any);
        }
    }, [Boolean(edConfigs)]);
    const rootConfig = (edConfigs || []).find(
        (i: any) => i.type === CONFIG_TYPES.ROOT && i.value === "root"
    )?.config || {};
    return {
        date_format: (typeof rootConfig.date_format === "string" && rootConfig.date_format)
            || _ED_HARDCODED_DATE_FORMAT,
        datetime_format: (typeof rootConfig.datetime_format === "string" && rootConfig.datetime_format)
            || _ED_HARDCODED_DATETIME_FORMAT,
    };
}
